// src/components/TikTokSection.js
// Standalone TikTok embed section — powered by a Google Sheet.
// Uses TikTok's official oEmbed API for rich previews with thumbnails.
// Drop <TikTokSection /> into any page; it renders nothing if the
// sheet isn't configured or has no rows.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Container, CircularProgress } from '@mui/material';
import { fetchTikToksFromSheet } from '../utils/googleApi';

// Individual TikTok embed card that fetches its own oEmbed HTML
const TikTokCard = ({ video, index }) => {
  const containerRef = useRef(null);
  const [embedHtml, setEmbedHtml] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!video.url) return;

    // Use TikTok's oEmbed API to get the official embed HTML
    const oembedUrl = `https://www.tiktok.com/oembed?url=${encodeURIComponent(video.url)}`;

    fetch(oembedUrl)
      .then(res => res.json())
      .then(data => {
        if (data.html) {
          setEmbedHtml(data.html);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true));
  }, [video.url]);

  // After injecting the oEmbed HTML, load/reload TikTok's embed script
  const loadEmbedScript = useCallback(() => {
    if (!containerRef.current) return;

    // Remove any existing TikTok script so it re-processes new embeds
    const existingScript = document.querySelector('script[src*="tiktok.com/embed.js"]');
    if (existingScript) existingScript.remove();

    // Reset TikTok's internal state if it exists
    if (window.tiktokEmbed) {
      delete window.tiktokEmbed;
    }

    const script = document.createElement('script');
    script.src = 'https://www.tiktok.com/embed.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (embedHtml) {
      // Small delay to ensure the HTML is in the DOM before the script runs
      const timer = setTimeout(loadEmbedScript, 100);
      return () => clearTimeout(timer);
    }
  }, [embedHtml, loadEmbedScript]);

  if (error) {
    return (
      <Box
        component="a"
        href={video.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          width: '100%',
          maxWidth: 340,
          height: 200,
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textDecoration: 'none',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-2px)' },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            color: 'black',
            fontWeight: 'bold',
            fontSize: '14px',
          }}
        >
          Watch on TikTok &rarr;
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 340,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {embedHtml ? (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            '& blockquote': { margin: '0 !important', maxWidth: '100% !important' },
            '& iframe': { maxWidth: '100% !important' },
          }}
          dangerouslySetInnerHTML={{ __html: embedHtml }}
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: 'rgba(0,0,0,0.3)' }} />
        </Box>
      )}

      {video.caption && (
        <Typography
          sx={{
            mt: 1.5,
            fontFamily: 'Roboto, sans-serif',
            color: 'rgba(0,0,0,0.6)',
            fontSize: '14px',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {video.caption}
        </Typography>
      )}
    </Box>
  );
};

const TikTokSection = () => {
  const [videos, setVideos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sheetId = process.env.REACT_APP_GOOGLE_SHEET_TIKTOK_ID;
    if (!sheetId) {
      setLoading(false);
      return;
    }

    fetchTikToksFromSheet(sheetId)
      .then(data => setVideos(data))
      .catch(err => console.error('[LFBBQ] TikTok section error:', err))
      .finally(() => setLoading(false));
  }, []);

  // Don't render anything if not configured or no videos
  if (!loading && (!videos || videos.length === 0)) return null;

  return (
    <Box sx={{ backgroundColor: '#F6F4F1', py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            mb: 1,
            fontFamily: '"Audiowide", sans-serif',
            fontWeight: 'normal',
            color: 'black',
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}
        >
          Follow the Smoke
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            mb: 4,
            fontFamily: 'Roboto, sans-serif',
            color: 'rgba(0,0,0,0.5)',
            fontSize: '16px',
          }}
        >
          Catch us on TikTok
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: 'black' }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: videos.length >= 3 ? 'repeat(3, 1fr)' : `repeat(${videos.length}, 1fr)`,
              },
              gap: 3,
              justifyItems: 'center',
            }}
          >
            {videos.map((video, idx) => (
              <TikTokCard key={video.videoId || idx} video={video} index={idx} />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TikTokSection;
